import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAddLinkMutation, useLinksQuery, useRemoveLinkMutation } from '@platform/state/api';
import type { ILink, ISearchHit, LinkType } from '@platform/core/types';
import { ErrorBanner, Spinner } from '@platform/ui';
import { LinkSearchDialog } from '../fields/lookup/LinkSearchDialog';
import type { IRelatedItemsTabProps } from './IRelatedItemsTabProps';
import { LINK_ORDER, linkTypeSummary, sortLinks } from './related/linkOrder';
import { RelatedFinder } from './related/RelatedFinder';
import { RelatedRow } from './related/RelatedRow';
import { useForeignRecords } from './related/useForeignRecords';
import { useRecordRefresh } from './useRecordRefresh';

/**
 * Вкладка «Пов’язані елементи» (UIUX forms/related-items, прототип v4): панель «N пов’язаних елементів · тип зв’язку · пошук елемента
 * будь-якого модуля (… — розширений пошук) · Зв’язати елемент»; чипи типів із лічильниками; рядки за порядком типів зв'язку. Кожен
 * зв'язок пишеться в історію обох записів.
 */
export function RelatedItemsTab({ ws, record }: IRelatedItemsTabProps) {
  const { t } = useTranslation();
  const { data, isLoading } = useLinksQuery(record.id);
  const [add, { error, isLoading: adding }] = useAddLinkMutation();
  const [remove, { error: removeError }] = useRemoveLinkMutation();
  const refresh = useRecordRefresh(record);
  const [linkType, setLinkType] = useState<LinkType>('relates');
  const [picked, setPicked] = useState<ISearchHit | null>(null);
  const [advanced, setAdvanced] = useState(false);
  const links = useMemo(() => sortLinks(data ?? []), [data]);
  const foreign = useForeignRecords(ws, useMemo(() => links.map((l) => l.otherModule), [links]));
  const exclude = useMemo(() => new Set([record.id, ...links.map((l) => l.otherRecordId)]), [record.id, links]);
  const canEdit = record.permissions?.edit !== false;
  const link = () => { if (picked) void add({ recordId: record.id, body: { toRecordId: picked.recordId, linkType } }).unwrap().then(() => { setPicked(null); refresh(); }).catch(() => undefined); };
  const unlink = (l: ILink) => void remove({ recordId: record.id, linkId: l.id, otherId: l.otherRecordId }).unwrap().then(refresh).catch(() => undefined);
  if (isLoading) return <Spinner />;
  return (
    <div className="rtab-related">
      <div className="rtab-bar" role="toolbar" aria-label={t('recordPage.related.actions')}>
        <span className="rtab-bar__count">{t('recordPage.related.count', { count: links.length })}</span>
        {canEdit && (
          <>
            <select className="rtab-select" aria-label={t('recordPage.related.linkType')} value={linkType} onChange={(e) => setLinkType(e.target.value as LinkType)}>
              {LINK_ORDER.map((x) => <option key={x} value={x}>{t(`recordPage.linkTypes.${x}`)}</option>)}
            </select>
            <RelatedFinder ws={ws} exclude={exclude} picked={picked} onPick={setPicked} onAdvanced={() => setAdvanced(true)} />
            <button type="button" className="rtab-btn rtab-btn--primary rtab-btn--link" disabled={!picked || adding} onClick={link}>{t('recordPage.related.linkItem')}</button>
          </>
        )}
      </div>
      <ErrorBanner error={error ?? removeError} />
      <div className="rtab-box rtab-box--related">
        {links.length > 0 && (
          <div className="rtab-summary" aria-label={t('recordPage.related.summary')}>
            {linkTypeSummary(links).map((s) => (
              <span key={s.type} className="rtab-summary__chip"><span className={`rtab-summary__tone link-tone--${s.type}`} />{t(`recordPage.linkTypes.${s.type}`)} · {s.count}</span>
            ))}
          </div>
        )}
        {links.map((l) => <RelatedRow key={l.id} link={l} foreign={foreign} canEdit={canEdit} onUnlink={unlink} />)}
        {!links.length && <p className="rtab-empty">{t('recordPage.related.empty')}</p>}
      </div>
      {advanced && <LinkSearchDialog ws={ws} excludeIds={[...exclude]} onPick={(hit) => { setPicked(hit); setAdvanced(false); }} onClose={() => setAdvanced(false)} />}
    </div>
  );
}
